/**
 * Billing recovery UI / redirects apply only to RepuAgency (Cashier default + pivots).
 * Other products (ReplyEngine lifetime, Social Review, etc.) must not trigger recovery.
 */

/**
 * @param {{ past_due?: unknown }} plan
 */
export function isPastDuePivot(plan) {
  if (!plan) return false
  const raw = plan.past_due
  if (raw === true || raw === 1 || raw === '1') return true
  if (raw === false || raw === 0 || raw === '0' || raw === null || raw === undefined) {
    return false
  }
  const normalized = String(raw).toLowerCase().trim()
  return ['true', 'yes', 'y', 'past_due', 'past due'].includes(normalized)
}

/** `users_plan` row whose plan is RepuAgency (product_type or bundle). */
export function pivotIsRepuAgencyPlanRow(row) {
  const p = row && row.plan
  if (!p) return false
  if (p.product_type === 'RepuAgency') return true
  const pp = p.planProducts
  return Array.isArray(pp) && pp.includes('RepuAgency')
}

export function userHasPastDueRepuAgencyPlan(user) {
  if (!user || !user.user_plans || !user.user_plans.length) return false
  return user.user_plans.some((row) => pivotIsRepuAgencyPlanRow(row) && isPastDuePivot(row))
}

/** Any past_due pivot (for subscription page copy / labels, not recovery gating). */
export function userHasPastDuePlanRow(user) {
  if (!user || !user.user_plans || !user.user_plans.length) return false
  return user.user_plans.some((plan) => isPastDuePivot(plan))
}

/**
 * True when agency should see RepuAgency billing recovery (modal, subscription redirect).
 */
export function agencyBillingRecoveryRedirect(user) {
  if (!user || !user.agency || user.agency.free_fee) return false

  const problemStatuses = [
    'past_due',
    'unpaid',
    'canceled',
    'cancelled',
    'incomplete',
    'incomplete_expired',
  ]
  const stripeIsProblematic = (raw) => {
    if (raw === null || raw === undefined || raw === '') return false
    const v = String(raw).toLowerCase().trim()
    return problemStatuses.includes(v)
  }

  const preview = user.recovery_plan_preview || user.recoveryPlanPreview
  const previewPt =
    preview && (preview.product_type || (preview.db_plan && preview.db_plan.product_type))
  const pastDueRepuAgency = userHasPastDueRepuAgencyPlan(user)

  const inRepuAgencyRecoveryScope = previewPt === 'RepuAgency' || pastDueRepuAgency
  if (!inRepuAgencyRecoveryScope) {
    return false
  }

  if (stripeIsProblematic(user.recovery_default_subscription_status)) return true

  if (user.subscribed) {
    if (user.on_trial && !userHasPastDueRepuAgencyPlan(user)) return false
    const st = user.stripe_status ? String(user.stripe_status).toLowerCase().trim() : ''
    const agencySt = user.agency.stripe_status
      ? String(user.agency.stripe_status).toLowerCase().trim()
      : ''
    const defaultRecoverySt = user.recovery_default_subscription_status
      ? String(user.recovery_default_subscription_status).toLowerCase().trim()
      : ''
    const userStripeHealthy = st === 'active' || st === 'trialing'
    const agencyStripeHealthy = !agencySt || agencySt === 'active' || agencySt === 'trialing'
    const recoveryDefaultHealthy =
      !defaultRecoverySt || defaultRecoverySt === 'active' || defaultRecoverySt === 'trialing'
    if (
      userStripeHealthy &&
      agencyStripeHealthy &&
      recoveryDefaultHealthy
    ) {
      return false
    }
  }

  // Past-due pivot rows can be historical after successful re-subscribe.
  // Keep recovery only for currently unsubscribed users.
  if (!user.subscribed && userHasPastDueRepuAgencyPlan(user)) return true
  if (
    user.last_plan_id &&
    !user.subscribed &&
    (previewPt === 'RepuAgency' || pastDueRepuAgency || user.has_repu_agency)
  ) {
    return true
  }

  // `stripe_status` can reflect Cashier's first `default` row (often stale/canceled) while RepuAgency bills on `repuagency`.
  if (stripeIsProblematic(user.stripe_status) && !user.subscribed) return true
  if (stripeIsProblematic(user.agency && user.agency.stripe_status) && !user.subscribed) return true

  return false
}

/** Routes where agency may work through billing without forced redirect to subscription. */
export function isAgencyBillingExemptRouteName(name) {
  if (!name) return false
  const s = String(name)
  return (
    s === 'agency.subscription' ||
    s === 'agency.profile' ||
    s.startsWith('agency.payment-methods')
  )
}
